import React, { useRef, useState } from "react";
import * as R from "ramda";
import { connect } from "react-redux";
import PasswordChecklist from "react-password-checklist";
import { debounce } from "utils/General";
import { Div } from "components/Base";
import resolveEditorProps from "components/Global/Editors/utils/resolveEditorProps";
import resolveEditor from "components/Global/StandAloneEditors/utils/resolveEditor";
import { getters } from "Portal/CallForPapersForm/model";
import { noop } from "utils/General";
import PasswordChecklistConfig from "utils/password-checklist-config";
import CSSModules from "react-css-modules";
import css from "./styles.scss";
import cn from "classnames";
import { getQuestionLabelColor, getQuestionLabelSize } from "../selectors";

import { MODULES_TINY_MCE, FORMATS_TINY_MCE } from "../constants";

import { actions as SnackbarActions } from "ui-kit/Snackbar/model";
import { SEVERITY } from "ui-kit/Alert/constants";

const decorate = connect(
  state => ({
    selectedSpeakerId: getters.selectedSpeakerId(state),
    questionLabelColor: getQuestionLabelColor(state),
    questionLabelSize: getQuestionLabelSize(state),
    eventTimezone: getters.eventTimezone(state)
  }),
  { showSnackbar: SnackbarActions.show }
);

const Field = ({
  field = null,
  values = [],
  updateValues = noop,
  showPasswordHint = true,
  onPasswordChecklistChange = noop,
  onBlur = noop,
  onFieldBlur = noop,
  selectedSpeakerId = null,
  instanceId = null,
  emailError = "",
  questionLabelColor,
  questionLabelSize,
  isSecondaryContactField = false,
  showSnackbar = noop,
  eventTimezone
}) => {
  if (!field) return null;

  const maxLength = R.pathOr(
    field.type === "text" ? "255" : "",
    ["settings", "maxLength"],
    field
  );

  const [isFocused, setIsFocused] = useState(false);

  const Editor = resolveEditor(field);
  const debounceOnChange = debounce(updateValues, 100);

  let refContainerFieldEditor = useRef(null);
  let refLabelFieldContainer = useRef(null);

  const onChangeEditor = (value, field) => {
    // remove previous error classes added
    if (!isSecondaryContactField) {
      if (refContainerFieldEditor?.current) {
        refContainerFieldEditor.current.classList.remove(
          css.invalidFieldEditor
        );
      }
      if (refLabelFieldContainer?.current) {
        refLabelFieldContainer.current.classList.remove(css.invalidFieldLabel);
      }
    }

    if (field.type === "email") {
      updateValues({ selectedSpeakerId, value, id: field.id });
    } else {
      debounceOnChange({ selectedSpeakerId, value, id: field.id });
    }
  };

  const onFileSelected = (file = {}, accept = []) => {
    const isAccepted = accept.some(type => {
      if (type.includes("*")) {
        const [mainType] = type.split("/");
        return file.mimetype.startsWith(mainType);
      }
      return file.mimetype === type;
    });
    if (!isAccepted && R.length(accept) > 0) {
      const { filename: name = "" } = file;
      showSnackbar({
        id: "not-accepted-file-type",
        severity: SEVERITY.ERROR,
        message: `File ${name} is not accepted file type. The accepted file types are ${accept.join(
          ", "
        )}`,
        autoHideDuration: false,
        buttonAction: { id: "ok-not-accepted-file-type", name: "Ok" }
      });
    }
  };

  return (
    <label
      onBlur={onBlur}
      aria-label={field.name}
      aria-required={field.required}
      id={`field-${field.id}-${selectedSpeakerId}`}
      onClick={e => {
        if (isFocused) {
          e.stopPropagation();
          e.preventDefault();
        }
      }}
    >
      <Div className="lennd-valid" mb={3}>
        <div
          className={cn(css.fieldLabel, {
            [css.validFieldLabel]: field.isValid,
            [css.invalidFieldLabel]: !field.isValid
          })}
          ref={refLabelFieldContainer}
        >
          <span
            style={{
              color: questionLabelColor,
              fontSize: questionLabelSize
            }}
          >
            <Div
              className={css.htmlDisplay}
              dangerouslySetInnerHTML={{ __html: field.name }}
            />
          </span>

          <Div
            style={{ opacity: field.required ? 1 : 0 }}
            color={"danger9"}
            fw={3}
            ml={1}
            fs={5}
          >
            *
          </Div>
        </div>
        {field?.settings?.description?.length ? (
          <Div
            fs={2}
            color="gray6"
            mb={1}
            style={{
              whiteSpace: "pre-line"
            }}
            className={css.htmlDisplay}
            dangerouslySetInnerHTML={{ __html: field.settings.description }}
          />
        ) : null}
        <div
          className={cn(css.field, "editor-field-container", {
            [css.invalidFieldEditor]: !field.isValid
          })}
          ref={refContainerFieldEditor}
        >
          <Editor
            tabIndex={0}
            aria-required={field.required}
            continuous={true}
            onFocus={() => setIsFocused(true)}
            onBlur={value => {
              if (onFieldBlur) {
                onFieldBlur(value, field);
              }

              // we disable clicking the label by 500ms to avoid multiple popups with unique contacts
              setTimeout(() => {
                setIsFocused(false);
              }, 500);
            }}
            onChange={value => onChangeEditor(value, field)}
            value={
              field.disabled && field.showObfuscatedValue
                ? {
                    value: "●●●●●●●●●"
                  }
                : R.propOr("", field.id, values)
            }
            autocomplete={
              field.type === "password"
                ? "new-password"
                : `autocomplete_${field.id}`
            }
            disabled={field.disabled}
            maxLength={maxLength}
            {...resolveEditorProps(field, {})}
            instanceId={instanceId}
            hasClearButton={true}
            style={
              field.type === "email" && emailError.length > 0
                ? { border: "1px solid #e03131" }
                : {}
            }
            modules={MODULES_TINY_MCE}
            formats={FORMATS_TINY_MCE}
            onFileSelected={onFileSelected}
            eventTimezone={eventTimezone}
          />
        </div>
        {field.type === "email" && emailError.length ? (
          <div
            style={{
              fontSize: 12,
              padding: "4px 0 0 4px",
              color: "#e03131",
              fontWeight: 600
            }}
          >
            {emailError}
          </div>
        ) : null}
        {!R.isEmpty(maxLength) ? (
          <div
            style={{
              fontSize: 12,
              padding: "4px 0 0 4px",
              color: "gray7",
              fontWeight: 600
            }}
          >
            {field.type === "wysiwyg" ? (
              `Max character limit: ${maxLength}`
            ) : (
              <>
                {R.pathOr("", [field.id, "value"], values).length}/{maxLength}{" "}
                Character
                {R.pathOr("", [field.id, "value"], values).length !== 1
                  ? "s"
                  : ""}
              </>
            )}
          </div>
        ) : null}
        {field.id === "password" && showPasswordHint ? (
          <div
            style={{
              marginTop: "5px",
              marginBottom: "10px",
              fontSize: "14px",
              color: "#222"
            }}
          >
            <PasswordChecklist
              tabIndex={0}
              iconSize={12}
              rules={["minLength", "specialChar", "number", "capital"]}
              minLength={8}
              value={R.pathOr("", [field.id, "value"], values)}
              onChange={onPasswordChecklistChange}
              messages={PasswordChecklistConfig}
            />
          </div>
        ) : null}
      </Div>
    </label>
  );
};

export default decorate(CSSModules(Field, css));
